import React, { useEffect, useState, useRef, useCallback } from "react";
import Select, { SingleValue } from "react-select";
import { getReq } from "../../utils/request";
import { Contact } from "../../types/User";
import RatingBadge from "../Rating/RatingBadge";
import classes from "./contactPicker.module.scss";

interface ContactOption {
    value: Contact;
    label: string;
}

interface ContactPickerProps {
    value: Contact | null;
    onChange: (contact: Contact | null) => void;
    /** When true (default), shows rating badge + sorts by rating descending. */
    showRating?: boolean;
    /** Optionally restrict results to contacts of a specific organization. */
    organizationId?: number;
    placeholder?: string;
    disabled?: boolean;
    isClearable?: boolean;
    required?: boolean;
    id?: string;
    /** Optional title/wrapper hint */
    title?: string;
}

/**
 * Shared contact (operator) picker — same UX patterns as OrganizationPicker.
 *  - Each dropdown row: name + reference number + rating badge
 *  - Sort by rating descending when ratings are enabled
 *  - Selected value: name + rating badge inline
 *  - Debounced search via contacts/search/keyword endpoint
 */
const ContactPicker: React.FC<ContactPickerProps> = ({
    value,
    onChange,
    showRating = true,
    organizationId,
    placeholder = "Search contact...",
    disabled = false,
    isClearable = true,
    required = false,
    id,
    title,
}) => {
    const [keyword, setKeyword] = useState("");
    const [options, setOptions] = useState<ContactOption[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const fetchOptions = useCallback(
        async (q: string) => {
            if (!q || q.trim() === "") {
                setOptions([]);
                return;
            }
            setIsLoading(true);
            try {
                const url = `contacts/search/keyword?keyword=${encodeURIComponent(q)}`;
                const response = await getReq(url);
                if (response.status === 200 && Array.isArray(response.body)) {
                    let contacts: Contact[] = response.body;
                    if (organizationId !== undefined) {
                        contacts = contacts.filter(
                            (c) => c.organization?.organizationId === organizationId,
                        );
                    }
                    if (showRating) {
                        contacts.sort((a, b) => {
                            const ar = Number((a as any).averageRating ?? -1);
                            const br = Number((b as any).averageRating ?? -1);
                            if (ar !== br) return br - ar;
                            return (a.fullName || "").localeCompare(b.fullName || "");
                        });
                    } else {
                        contacts.sort((a, b) =>
                            (a.fullName || "").localeCompare(b.fullName || ""),
                        );
                    }
                    setOptions(
                        contacts.map((c) => ({
                            value: c,
                            label: c.fullName || `${c.firstName} ${c.lastName ?? ""}`.trim(),
                        })),
                    );
                }
            } finally {
                setIsLoading(false);
            }
        },
        [showRating, organizationId],
    );

    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            fetchOptions(keyword);
        }, 250);
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, [keyword, fetchOptions]);

    const selectedOption: ContactOption | null = value
        ? { value, label: value.fullName || `${value.firstName} ${value.lastName ?? ""}`.trim() }
        : null;

    const formatOptionLabel = (option: ContactOption, meta: { context: string }) => {
        const c = option.value;
        if (meta.context === "menu") {
            const refNumber = c.referenceNumber;
            const orgName = c.organization?.name ?? null;
            return (
                <div className={classes.optionRow}>
                    <div className={classes.optionMain}>
                        <span className={classes.optionName}>
                            {c.fullName || `${c.firstName} ${c.lastName ?? ""}`.trim()}
                        </span>
                        <span className={classes.optionRef}>
                            {refNumber}
                            {orgName && <> · {orgName}</>}
                        </span>
                    </div>
                    {showRating && (
                        <RatingBadge
                            average={(c as any).averageRating ?? null}
                            total={(c as any).totalRatings ?? 0}
                            compact
                        />
                    )}
                </div>
            );
        }
        return <span>{option.label}</span>;
    };

    return (
        <div className={classes.pickerWrapper} title={title}>
            <div className={classes.pickerControl}>
                <Select<ContactOption>
                    aria-label="Select a contact"
                    inputId={id}
                    value={selectedOption}
                    inputValue={keyword}
                    onInputChange={(v, meta) => {
                        if (meta.action === "input-change") {
                            setKeyword(v);
                        } else if (
                            meta.action === "set-value" ||
                            meta.action === "menu-close" ||
                            meta.action === "input-blur"
                        ) {
                            setKeyword("");
                        }
                    }}
                    options={options}
                    isLoading={isLoading}
                    placeholder={placeholder}
                    onChange={(opt: SingleValue<ContactOption>) => {
                        onChange(opt?.value ?? null);
                        setKeyword("");
                    }}
                    noOptionsMessage={({ inputValue }) =>
                        inputValue.trim() === "" ? "Start typing to search" : "No data found"
                    }
                    formatOptionLabel={formatOptionLabel}
                    className="custom-select"
                    classNamePrefix="custom-select"
                    isDisabled={disabled}
                    isClearable={isClearable}
                    required={required}
                />
            </div>
            {value && showRating && (
                <RatingBadge
                    average={(value as any).averageRating ?? null}
                    total={(value as any).totalRatings ?? 0}
                    compact
                />
            )}
        </div>
    );
};

export default ContactPicker;
